
import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

import { analytics } from '@/services/analyticsService';
import { AuditService } from '@/services/auditService';

import { toast } from 'sonner';
import { Tables } from '@/integrations/supabase/types';
import { logger } from '@/utils/logger';

// Profile type from database
type Profile = Tables<'profiles'>;

// AppUser extends Supabase User with profile data
interface AppUser extends User {
  profile?: Partial<Profile>;
}

// Normalized user data from database
interface UserData {
  profile: Profile | null;
  roles: string[];
}

interface AuthContextType {
  user: AppUser | null;
  session: Session | null;
  currentRole: string | null;
  availableRoles: string[];
  rolesLoaded: boolean;
  isLoading: boolean;
  hasBankDetails: boolean | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithProvider: (provider: 'google' | 'facebook') => Promise<void>;
  signup: (email: string, password: string, name?: string, role?: string, metadata?: Record<string, string>) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  switchRole: (role: string) => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: () => boolean;
  isSeller: () => boolean;
  isBuyer: () => boolean;
  refreshUser: () => Promise<void>;
  refreshBankDetails: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_PREF_STORAGE = 'twiceread_preferred_role';

// === Pure helper functions (no side effects) ===

const getSavedRolePreference = (): string | null => {
  try {
    return localStorage.getItem(ROLE_PREF_STORAGE);
  } catch {
    return null;
  }
};

const saveRolePreference = (role: string): void => {
  try {
    localStorage.setItem(ROLE_PREF_STORAGE, role);
  } catch {
    // Ignore storage errors
  }
};

const clearRolePreference = (): void => {
  try {
    localStorage.removeItem(ROLE_PREF_STORAGE);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Determines the active role based on available roles and saved preference.
 * Single place for role decision logic.
 */
const determineActiveRole = (roles: string[], savedPreference: string | null): string => {
  // If user has a saved preference AND it's in their available roles, use it
  if (savedPreference && roles.includes(savedPreference)) {
    return savedPreference;
  }
  
  // Priority: admin > seller (if only role) > buyer > seller > default buyer
  if (roles.includes('admin')) return 'admin';
  if (roles.length === 1 && roles.includes('seller')) return 'seller';
  if (roles.includes('buyer')) return 'buyer';
  if (roles.includes('seller')) return 'seller';
  
  return 'buyer'; // Default fallback
};

/**
 * Fetches and normalizes user data from database.
 * Single function for all data fetching - no retries (leave to network layer).
 */
const fetchUserData = async (userId: string): Promise<UserData> => {
  const [profileResponse, rolesResponse] = await Promise.all([
    supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle(),
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
  ]);

  if (profileResponse.error && profileResponse.error.code !== 'PGRST116') {
    logger.error('Error fetching user profile:', profileResponse.error);
  }

  if (rolesResponse.error) {
    logger.error('Error fetching user roles:', rolesResponse.error);
  }

  return {
    profile: profileResponse.data || null,
    roles: rolesResponse.data?.map(r => r.role) || []
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState<boolean | null>(null);
  // Prevent duplicate profile fetches
  const fetchingForUser = useRef<string | null>(null);

  /**
   * Loads user profile and roles, updates state accordingly.
   * Called on sign-in and for manual refresh.
   */
  const loadUserProfile = async (userId: string): Promise<void> => {
    // Prevent duplicate concurrent fetches for same user
    if (fetchingForUser.current === userId) {
      return;
    }
    fetchingForUser.current = userId;

    try {
      const userData = await fetchUserData(userId);
      
      // Only update if we got roles (don't clear existing on empty response)
      if (userData.roles.length > 0 || availableRoles.length === 0) {
        setAvailableRoles(userData.roles);
      }
      
      // Determine active role
      const activeRole = determineActiveRole(
        userData.roles.length > 0 ? userData.roles : availableRoles,
        getSavedRolePreference()
      );
      setCurrentRole(activeRole);
      setRolesLoaded(true);

      // Merge profile data into user object
      if (userData.profile) {
        setUser(prev => prev ? {
          ...prev,
          profile: userData.profile
        } : null);
      }
    } catch (error: unknown) {
      logger.error('Error loading user profile:', error);
      // Set a default role on error to unblock UI (functional setter avoids stale closure)
      setCurrentRole(prev => prev ?? 'buyer');
      setRolesLoaded(true);
    } finally {
      fetchingForUser.current = null;
    }
  };

  useEffect(() => {
    // Track which user id we've already loaded a profile for, so token refreshes
    // and tab-focus SIGNED_IN events don't trigger a full state churn that
    // re-mounts the dashboard tree (which would close any open chat / reset tabs).
    let loadedForUserId: string | null = null;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        // Synchronous state updates only
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (event === 'SIGNED_IN' && currentSession?.user) {
          // Check for email verification flag
          const pendingVerification = sessionStorage.getItem('twiceread_pending_email_verification');
          if (pendingVerification) {
            sessionStorage.removeItem('twiceread_pending_email_verification');
            toast.success('Email verified successfully! Welcome to Twice Read.');
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }
          
          // Skip profile reload if we've already loaded for this user.
          // SIGNED_IN can fire on tab refocus / token refresh in some Supabase
          // SDK versions; reloading would churn currentRole/availableRoles and
          // re-mount role-gated subtrees (closing chat, resetting tabs).
          if (loadedForUserId !== currentSession.user.id) {
            loadedForUserId = currentSession.user.id;
            loadUserProfile(currentSession.user.id);
            analytics.trackLogin();
            AuditService.logUserAction('login', currentSession.user.id);
          }
        } else if (event === 'SIGNED_OUT') {
          // Reset state on sign out
          loadedForUserId = null;
          setCurrentRole(null);
          setAvailableRoles([]);
          setRolesLoaded(false);
          
          analytics.trackLogout();
          AuditService.logUserAction('logout');
        }
        
        setIsLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setIsLoading(false);
      
      if (initialSession?.user) {
        loadedForUserId = initialSession.user.id;
        loadUserProfile(initialSession.user.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // === Auth methods ===

  const login = async (email: string, password: string): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Enforce account suspension: block suspended sellers from accessing the app
    try {
      const { data: status } = await supabase.rpc('get_my_account_status');
      if (status === 'suspended') {
        await supabase.auth.signOut();
        throw new Error('Your account has been suspended. Please contact support for assistance.');
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes('suspended')) {
        throw err;
      }
      // Non-fatal: status check failure shouldn't block login
    }
  };

  const loginWithProvider = async (provider: 'google' | 'facebook'): Promise<void> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (error) throw error;
  };

  const signup = async (email: string, password: string, name?: string, role?: string, metadata?: Record<string, string>): Promise<void> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, role: role || 'buyer', ...metadata },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (error) throw error;
    
    if (data.user) {
      analytics.trackEvent('signup', 'authentication', { method: 'email' });
      AuditService.logUserAction('signup', data.user.id, { role: role || 'buyer' });
      
      // Welcome email role: derived from the signup form's `role` argument
      // (which we just wrote into user_metadata above). This is the only
      // legitimate place to read role from form/metadata — it's the user's
      // first session and `user_roles` rows are populated by a downstream
      // trigger. Any OTHER welcome trigger added later (e.g. after upgrade)
      // MUST read from the `user_roles` table via has_role(), NOT metadata,
      // because a buyer who upgrades to seller keeps the old metadata value.
      const welcomeTemplate = (role || 'buyer') === 'seller' ? 'welcome_seller' : 'welcome_buyer';
      supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: welcomeTemplate,
          to: email,
          userId: data.user.id,
          idempotencyKey: `welcome:${data.user.id}`,
          data: { name: name || email.split('@')[0], email, role: role || 'buyer' },
        },
      }).catch((err: unknown) => logger.error('Failed to send welcome email:', err instanceof Error ? err.message : 'unknown'));

    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      if (error.message.includes('User not found')) {
        throw new Error('No account found with this email address.');
      }
      throw error;
    }
    // Note: Supabase sends the reset email with the secure token link.
    // We do NOT send a custom duplicate email — the custom one had no token
    // and produced a broken second message in the user's inbox.
  };

  const logout = async (): Promise<void> => {
    clearRolePreference();
    // Best-effort release of all the user's cart reservations before signing out
    try {
      await supabase.rpc('release_all_my_reservations');
    } catch (e) {
      // Non-fatal: TTL will release them within 30 minutes anyway
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // === Role checking methods ===

  const hasRole = (role: string): boolean => {
    if (!user || !role) return false;
    return availableRoles.includes(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user || !roles || roles.length === 0) return false;
    return roles.some(r => availableRoles.includes(r));
  };

  const isAdmin = (): boolean => hasRole('admin');
  const isSeller = (): boolean => hasRole('seller');
  const isBuyer = (): boolean => hasRole('buyer');

  const switchRole = async (role: string): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    if (!availableRoles.includes(role)) {
      throw new Error(`You don't have permission to switch to ${role} role`);
    }
    
    const previousRole = currentRole;
    setCurrentRole(role);
    saveRolePreference(role);
    
    setTimeout(() => {
      analytics.trackEvent('role_switch', 'authentication', { 
        from_role: previousRole,
        to_role: role 
      });
      AuditService.logUserAction('role_switch', user.id, { 
        from_role: previousRole, 
        to_role: role 
      });
    }, 0);
    
    toast.success(`Switched to ${role} dashboard`);
  };

  const checkBankDetails = async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('seller_payment_details')
        .select('id')
        .eq('seller_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        logger.error('Error checking payment details:', error);
        setHasBankDetails(false);
        return;
      }
      setHasBankDetails(!!data);
    } catch (error: unknown) {
      logger.error('Error checking payment details:', error);
      setHasBankDetails(false);
    }
  };

  const refreshBankDetails = async (): Promise<void> => {
    if (user) {
      await checkBankDetails(user.id);
    }
  };

  // Fetch bank details when user or roles change (only for sellers)
  useEffect(() => {
    if (user && availableRoles.includes('seller')) {
      checkBankDetails(user.id);
    } else {
      setHasBankDetails(null);
    }
  }, [user?.id, availableRoles]);

  const refreshUser = async (): Promise<void> => {
    if (user) {
      // Clear the fetch guard to allow refresh
      fetchingForUser.current = null;
      await loadUserProfile(user.id);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    currentRole,
    availableRoles,
    rolesLoaded,
    isLoading,
    hasBankDetails,
    login,
    loginWithProvider,
    signup,
    logout,
    resetPassword,
    switchRole,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSeller,
    isBuyer,
    refreshUser,
    refreshBankDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
